import { apiDownloadFile, apiFetch } from "@/services/apiClient";
import { buildDrillThroughParams } from "@/lib/drillThroughTableQuery";
import { appendSortParams, type SortState } from "@/lib/tableSort";
import { API_URL } from "@/lib/utils";

export interface DebtorFilters {
  service_lines: string[];
  legacy_locations: string[];
  managers: string[];
  assignment_categories: string[];
  age_band_names: string[];
  date_from: string | null;
  date_to: string | null;
}

export interface DebtorFilterOptions {
  service_lines: string[];
  legacy_locations: string[];
  managers: string[];
  assignment_categories: string[];
  age_band_names: string[];
  min_month: string | null;
  max_month: string | null;
}

export type DebtorsAgeBandKey =
  | "age_0_30"
  | "age_31_60"
  | "age_61_90"
  | "age_91_120"
  | "age_120_180"
  | "age_180_plus";

export type DebtorsDrillThroughRow = Record<string, string | number | null | undefined> & {
  total?: number | null;
};

export type DebtorsAssignmentEntry = Record<string, string | number | null | undefined> & {
  assignment_name?: string | null;
  grade?: string | null;
  total?: number | null;
};

export type DebtorsClientAssignmentGroup = {
  client_name?: string | null;
  assignments: DebtorsAssignmentEntry[];
  total: Record<string, string | number | null | undefined> & { label?: string | null; total?: number | null };
};

export interface DebtorsDrillThroughResponse {
  as_at_date: string | null;
  monthLabel: string | null;
  age_bands: Array<string | { key?: string; label?: string; name?: string }>;
  total_aged_debt_gbp: number;
  row_count: number;
  by_manager: DebtorsDrillThroughRow[];
  by_assignment_manager: DebtorsDrillThroughRow[];
  by_client_assignment: DebtorsClientAssignmentGroup[];
  assignment_manager_total_rows: number;
  client_assignment_total_rows: number;
  data?: DebtorsDrillThroughRow[];
}

function buildQs(f: DebtorFilters): string {
  const p = new URLSearchParams();
  f.service_lines.forEach(v => p.append("service_lines", v));
  f.legacy_locations.forEach(v => p.append("legacy_locations", v));
  f.managers.forEach(v => p.append("managers", v));
  f.assignment_categories.forEach(v => p.append("assignment_categories", v));
  f.age_band_names.forEach(v => p.append("age_band_names", v));
  if (f.date_from) p.append("date_from", f.date_from);
  if (f.date_to)   p.append("date_to",   f.date_to);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await apiFetch(`${API_URL}${path}`, { method: "GET", signal });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export const fetchDebtorsFilterOptions = (filters: DebtorFilters = { service_lines: [], legacy_locations: [], managers: [], assignment_categories: [], age_band_names: [], date_from: null, date_to: null }, signal?: AbortSignal) =>
  getJson<Omit<DebtorFilterOptions, "min_month" | "max_month">>(`/debtors/filter-options${buildQs(filters)}`, signal);

export const fetchDebtorsDateRange = () =>
  getJson<{ min_month: string | null; max_month: string | null }>("/debtors/date-range");

export const fetchDebtorsMonthly = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; total_aged_debt_gbp: number }[] }>(`/debtors/monthly${buildQs(f)}`, signal);

export const fetchDebtorsMonthlyByAgeBand = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[] }>(`/debtors/monthly-by-age-band${buildQs(f)}`, signal);

export const fetchDebtorsRiskConcentration = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; risk_pct: number }[] }>(`/debtors/risk-concentration${buildQs(f)}`, signal);

export const fetchDebtorsByOfficeAgeBand = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[] }>(`/debtors/by-office-age-band${buildQs(f)}`, signal);

export const fetchDebtorsByServiceLine = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[]; series: string[] }>(`/debtors/by-service-line${buildQs(f)}`, signal);

export const fetchDebtorsByLocation = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: Record<string, number | string>[]; series: string[] }>(`/debtors/by-location${buildQs(f)}`, signal);

export const fetchDebtorsTopManagers = (f: DebtorFilters, limit = 10, signal?: AbortSignal) => {
  const qs = buildQs(f);
  return getJson<{ data: { manager: string; aged_debt_gbp: number }[] }>(
    `/debtors/top-managers${qs}${qs ? "&" : "?"}limit=${limit}`, signal);
};

export const fetchDebtorsAvgAge = (f: DebtorFilters, signal?: AbortSignal) =>
  getJson<{ data: { monthLabel: string; avg_age_days: number }[] }>(`/debtors/avg-age${buildQs(f)}`, signal);

export const DEBTORS_DRILL_FILTER_KEYS = [
  "service_lines",
  "legacy_locations",
  "managers",
  "assignment_categories",
  "age_band_names",
  "date_from",
  "date_to",
] as const;

export const fetchDebtorsCurrentDrillThrough = (
  month: string,
  filters?: Partial<DebtorFilters> & { managerSort?: SortState; clientSort?: SortState },
  signal?: AbortSignal,
  page?: { limit: number; skip: number },
) => {
  const { managerSort, clientSort, ...baseFilters } = filters ?? {};
  const params = buildDrillThroughParams({
    month,
    filters: baseFilters as Record<string, string[] | string | null | undefined>,
    filterKeys: [...DEBTORS_DRILL_FILTER_KEYS],
  });

  if (page) {
    params.set("limit", String(page.limit));
    params.set("skip", String(page.skip));
  }

  if (managerSort) {
    appendSortParams(params, managerSort, { sortByParam: "manager_sort_by", sortDirParam: "manager_sort_dir" });
  }
  if (clientSort) {
    appendSortParams(params, clientSort, { sortByParam: "client_sort_by", sortDirParam: "client_sort_dir" });
  }

  return getJson<DebtorsDrillThroughResponse>(
    `/debtors/drill-through?${params.toString()}`,
    signal,
  );
};

export const downloadDebtorsDrillThroughExcel = (
  month: string,
  filters?: Partial<DebtorFilters>,
  exportOptions: Array<"assignment_manager" | "client_assignment"> = [],
) => {
  const params = buildDrillThroughParams({
    month,
    filters: filters as Record<string, string[] | string | null | undefined>,
    filterKeys: [...DEBTORS_DRILL_FILTER_KEYS],
  });
  exportOptions.forEach((option) => params.append("export_options", option));

  return apiDownloadFile(
    `${API_URL}/debtors/drill-through/export?${params.toString()}`,
    exportOptions.length > 1 ? "Debtors Drill Through Exports.zip" : "debtors-drill-through.xlsx",
  );
};
